import { forwardRef, type SelectHTMLAttributes } from "react";
import { RequiredMark } from "./RequiredMark.js";

export type SelectOption = {
  value: string;
  label: string;
};

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> & {
  label: string;
  error?: string | undefined;
  options: ReadonlyArray<SelectOption>;
  // When set, renders a disabled empty option as the first item so the
  // user must explicitly pick a value. The placeholder text is shown
  // when the field is empty.
  placeholder?: string | undefined;
  required?: boolean;
};

export const Select = forwardRef<HTMLSelectElement, Props>(function Select(
  { label, error, id, required, options, placeholder, ...rest },
  ref,
) {
  const inputId = id ?? `select-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const errorId = error ? `${inputId}-err` : undefined;
  return (
    <div>
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-slate-700"
      >
        {label}
        {required ? <RequiredMark /> : null}
      </label>
      <select
        id={inputId}
        ref={ref}
        required={required}
        aria-required={required ? "true" : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={errorId}
        defaultValue={rest.defaultValue ?? ""}
        className={
          "field-input mt-1 block w-full rounded-md border bg-white px-3 py-2 " +
          "text-sm text-slate-900 shadow-sm " +
          "hover:border-slate-400 " +
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 " +
          (error ? "border-red-400" : "border-slate-300")
        }
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
});
